// src/screens/MapView.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  useMapEvent,
  useMap,
} from 'react-leaflet';
import logosImg from '../assets/web_logos_bandera.svg';
import L, { LatLngExpression, LatLngTuple, Map } from 'leaflet';
import 'leaflet.markercluster';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { useItinerary } from '../hooks/useItinerary';
import {
  Lugar,
  recalcularItinerario,
  obtenerDescripcionRuta,
  eliminarLugarItinerario,
  shareItinerary,
  obtenerItinerarioCompartido,
} from '../api/api';
import { obtenerRutaOSRM, RutaOSRMGeometry } from '../api/osrm';
import { houseIcon, getIconForLugarType } from '../icons';

import PlaceCard from '../components/PlaceCard';
import 'leaflet/dist/leaflet.css';
import styles from '../styles/MapView.module.css';
import { CARTO_ATTRIBUTION, CARTO_BASEMAP_URL } from '../config/maps';

import shp from 'shpjs';
import type { FeatureCollection } from 'geojson';

/* ------------------------------------------------------------------ */
/* UTILIDADES                                                        */
/* ------------------------------------------------------------------ */

/* 1) Cerrar pop-ups al pinchar sobre el mapa */
const MapClickHandler: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  useMapEvent('click', onClick);
  return null;
};

/* 2) Layer de clustering de POIs externos */
const ClusterLayer: React.FC<{
  puntos: Lugar[];
  selectedRoutePlaces: Lugar[];
  selectedNewPlace: Lugar | null;
  onMarkerClick: (l: Lugar) => void;
  onMarkerMouseOver: (l: Lugar) => void;
  onMarkerMouseOut: () => void;
}> = ({
  puntos,
  selectedRoutePlaces,
  selectedNewPlace,
  onMarkerClick,
  onMarkerMouseOver,
  onMarkerMouseOut,
}) => {
  const map = useMap();
  const layerRef = useRef<L.MarkerClusterGroup>();

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup();
    layerRef.current = clusterGroup;

    puntos.forEach((punto) => {
      const icon =
        selectedRoutePlaces.some((p) => p.id === punto.id) ||
        selectedNewPlace?.id === punto.id
          ? blueIcon
          : getIconForLugarType(punto.tipo);

      const marker = L.marker([punto.latitud, punto.longitud], { icon });

      marker.on('click', () => onMarkerClick(punto));
      marker.on('mouseover', () => onMarkerMouseOver(punto));
      marker.on('mouseout', () => onMarkerMouseOut());

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    return () => {
      map.removeLayer(clusterGroup);
    };
  }, [
    map,
    puntos,
    selectedRoutePlaces,
    selectedNewPlace,
    onMarkerClick,
    onMarkerMouseOver,
    onMarkerMouseOut,
  ]);

  return null;
};

/* 3) Extraer la primera foto disponible */
const getImageUrl = (fotos: any): string =>
  !fotos ? '' : Array.isArray(fotos) ? fotos[0] : fotos;

/* 4) Icono azul para “seleccionado” */
const blueIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  iconRetinaUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/* 5) Colores */
const colores = ['#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#e74c3c', '#1abc9c'];

/* 6) Crear icono numerado */
const createNumberedIcon = (numero: number, baseIcon: L.Icon, routeColor: string) =>
  new L.DivIcon({
    html: `<div class="${styles.numberedIconContainer}">
             <img src="${baseIcon.options.iconUrl}" alt="icon" />
             <span class="${styles.number}" style="color: ${routeColor};">${numero}</span>
           </div>`,
    className: styles.customNumberedDivIcon,
    iconSize: baseIcon.options.iconSize,
    iconAnchor: [12, 41],
    popupAnchor: [0, -40],
  });

/** Duración aproximada solo con horas HH:mm  */
const calcularDuracionRuta = (lugares: Lugar[]): string => {
  if (!lugares.length) return '—';

  const first = lugares[0];
  const last = lugares[lugares.length - 1];

  const isHHMM = (s?: string) => !!s && /^\d{2}:\d{2}$/.test(s);

  if (!isHHMM(first.horaLlegada) || !isHHMM(last.horaSalida)) return '—';

  const [h1, m1] = first.horaLlegada!.split(':').map(Number);
  const [h2, m2] = last.horaSalida!.split(':').map(Number);

  const startMin = h1 * 60 + m1;
  const endMin = h2 * 60 + m2;
  const diff = endMin - startMin;

  if (diff <= 0) return '—';
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};

/* 8) Hook para exponer la ref del mapa */
const SetMapRef: React.FC<{ mapRef: React.RefObject<Map> }> = ({ mapRef }) => {
  const map = useMap();
  useEffect(() => {
    (mapRef as React.MutableRefObject<Map | null>).current = map;
  }, [map, mapRef]);
  return null;
};

/* 9) Capa de sombreado desde Shapefile (no interactiva) */
const ShapefileOverlay: React.FC<{
  url: string;
  fitOnLoad?: boolean;
  style?: L.PathOptions;
  paneName?: string;
}> = ({ url, fitOnLoad = false, style, paneName = 'shade-pane' }) => {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  useEffect(() => {
    if (!map.getPane(paneName)) {
      const p = map.createPane(paneName);
      p.style.zIndex = '350';
      p.style.pointerEvents = 'none';
    }

    let cancelled = false;

    (async () => {
      try {
        const geojson = (await shp(url)) as FeatureCollection;
        if (cancelled) return;

        const layer = L.geoJSON(geojson, {
          pane: paneName,
          interactive: false,
          bubblingMouseEvents: false,
          style: {
            color: '#e74c3c',
            weight: 1,
            fillColor: '#e74c3c',
            fillOpacity: 0.35,
            ...style,
          },
        });

        layer.addTo(map);
        layerRef.current = layer;

        if (fitOnLoad) {
          const b = layer.getBounds();
          if (b.isValid()) map.fitBounds(b, { padding: [20, 20] });
        }
      } catch (e) {
        console.error('Error cargando shapefile:', e);
      }
    })();

    return () => {
      cancelled = true;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [url, fitOnLoad, paneName, map, style]);

  return null;
};

/* ------------------------------------------------------------------ */
/* COMPONENTE                                                         */
/* ------------------------------------------------------------------ */
const MapView: React.FC = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [note, setNote] = useState('');

  const navigate = useNavigate();
  const { shareId } = useParams<{ shareId?: string }>();

  const {
    itinerarios,
    puntosTuristicos,
    setItinerarios,
    startingPoint,
    dayStarts,
    dayEnds,
  } = useItinerary();

  const location = useLocation() as any;
  const mapRef = useRef<Map | null>(null) as React.MutableRefObject<Map | null>;

  /* ---- State ---- */
  const [rutasOSRM, setRutasOSRM] = useState<RutaOSRMGeometry[]>([]);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedRoutePlaces, setSelectedRoutePlaces] = useState<Lugar[]>([]);
  const [selectedNewPlace, setSelectedNewPlace] = useState<Lugar | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  /* Tooltips para los botones */
  const [showRecalculateTooltip, setShowRecalculateTooltip] = useState(false);
  const [showDeleteTooltip, setShowDeleteTooltip] = useState(false);
  const [showSustituirTooltip, setShowSustituirTooltip] = useState(false);

  const [descripciones, setDescripciones] = useState<Record<number, string>>({});
  const [isLoadingDescriptions, setIsLoadingDescriptions] = useState<Record<number, boolean>>({});
  const [draggedDay, setDraggedDay] = useState<number | null>(null);
  const [activePlace, setActivePlace] = useState<Lugar | null>(null);
  const [placeCardPlace, setPlaceCardPlace] = useState<Lugar | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);

  /* --- Cierre automático de popups al salir con el ratón --- */
  const closePopupTimer = useRef<number | null>(null);

  const cancelClosePopup = () => {
    if (closePopupTimer.current) {
      window.clearTimeout(closePopupTimer.current);
      closePopupTimer.current = null;
    }
  };

  const scheduleClosePopup = () => {
    cancelClosePopup();
    closePopupTimer.current = window.setTimeout(() => {
      setActivePlace(null);
    }, 150);
  };

  /* ---- Centro inicial ---- */
  const centroMapa: LatLngExpression = startingPoint
    ? [startingPoint.latitud, startingPoint.longitud]
    : puntosTuristicos.length
      ? [puntosTuristicos[0].latitud, puntosTuristicos[0].longitud]
      : [42.7654, -3.0924];

  /* Refs para hacer scroll a los items del itinerario */
  const itineraryItemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const lugaresEnItinerarios = new Set<number>();
  itinerarios.forEach((it) => it.lugares.forEach((l) => lugaresEnItinerarios.add(l.id)));

  const formatearHora = (fecha: Date | string | null | undefined): string => {
    if (!fecha) return 'N/A';
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return 'Hora inválida';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  /* ------------------------------------------------------------------ */
  /* 1) Cargar rutas OSRM                                                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchRutas = async () => {
      const geoms = await Promise.all(
        itinerarios.map(async (it) => {
          let coords: [number, number][] = it.lugares.map((l) => [l.latitud, l.longitud]);

          const dStart = dayStarts[it.dia];
          const dEnd = dayEnds[it.dia];
          if (dStart) coords = [[dStart.latitud, dStart.longitud], ...coords];
          if (dEnd) coords = [...coords, [dEnd.latitud, dEnd.longitud]];

          if (startingPoint) {
            coords = [
              [startingPoint.latitud, startingPoint.longitud],
              ...coords,
              [startingPoint.latitud, startingPoint.longitud],
            ];
          }

          return await obtenerRutaOSRM(coords);
        }),
      );

      setRutasOSRM(geoms.filter((g): g is RutaOSRMGeometry => !!g));
    };

    if (itinerarios.length) {
      fetchRutas();
      if (selectedDay === null) setSelectedDay(itinerarios[0].dia);
    } else {
      setRutasOSRM([]);
    }
  }, [itinerarios, startingPoint, dayStarts, dayEnds]);

  /* ------------------------------------------------------------------ */
  /* 2) Ajustar vista al seleccionar día                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (selectedDay === null || !mapRef.current) return;
    const idx = itinerarios.findIndex((it) => it.dia === selectedDay);
    if (idx < 0 || !rutasOSRM[idx]) return;
    const pos = rutasOSRM[idx].coordinates.map(([lon, lat]) => [lat, lon] as [number, number]);
    mapRef.current.fitBounds(L.latLngBounds(pos), {
      paddingTopLeft: [50, 50],
      paddingBottomRight: [50, 150],
    });
  }, [selectedDay, itinerarios, rutasOSRM]);

  /* 0) Si llegamos desde un enlace “compartido”, traemos los datos */
  useEffect(() => {
    if (!shareId) return;

    (async () => {
      try {
        const its = await obtenerItinerarioCompartido(shareId);
        setItinerarios(its);
        if (its.length) setSelectedDay(its[0].dia);
      } catch (err) {
        console.error('Error cargando itinerario compartido', err);
        alert('No se pudo cargar el itinerario compartido.');
      }
    })();
  }, [shareId]);

  /* ------------------------------------------------------------------ */
  /* 3) Limpiar selecciones al cambiar de día                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    setSelectedNewPlace(null);
    setSelectedRoutePlaces([]);
    setWarningMessage(null);
  }, [selectedDay]);

  /* ------------------------------------------------------------------ */
  /* 4) Obtener descripciones (solo una vez)                             */
  /* ------------------------------------------------------------------ */
  const gotDescriptions = useRef(false);
  useEffect(() => {
    if (gotDescriptions.current || !itinerarios.length) return;
    (async () => {
      setIsLoadingDescriptions(
        itinerarios.reduce((a, it) => ({ ...a, [it.dia]: true }), {} as Record<number, boolean>),
      );
      for (const it of itinerarios) {
        try {
          const d = await obtenerDescripcionRuta(it.lugares);
          setDescripciones((prev) => ({ ...prev, [it.dia]: d }));
        } catch {
          setDescripciones((prev) => ({ ...prev, [it.dia]: 'No se pudo obtener descripción.' }));
        } finally {
          setIsLoadingDescriptions((prev) => ({ ...prev, [it.dia]: false }));
        }
      }
      gotDescriptions.current = true;
    })();
  }, [itinerarios]);

  /* ------------------------------------------------------------------ */
  /*  Drag & drop días                                                   */
  /* ------------------------------------------------------------------ */
  const handleDragStart = (_e: React.DragEvent<HTMLElement>, day: number) => setDraggedDay(day);
  const handleDragOver = (e: React.DragEvent<HTMLElement>) => e.preventDefault();
  const handleDrop = (_e: React.DragEvent<HTMLElement>, targetDay: number) => {
    if (draggedDay === null || draggedDay === targetDay) return setDraggedDay(null);
    const i1 = itinerarios.findIndex((it) => it.dia === draggedDay);
    const i2 = itinerarios.findIndex((it) => it.dia === targetDay);
    if (i1 < 0 || i2 < 0) return setDraggedDay(null);
    const copy = [...itinerarios];
    [copy[i1].dia, copy[i2].dia] = [copy[i2].dia, copy[i1].dia];
    setDescripciones((d) => ({
      ...d,
      [draggedDay]: d[targetDay],
      [targetDay]: d[draggedDay],
    }));
    copy.sort((a, b) => a.dia - b.dia);
    setItinerarios(copy);
    setDraggedDay(null);
  };

  /* ------------------------------------------------------------------ */
  /*  Selecciones de markers                                             */
  /* ------------------------------------------------------------------ */
  const handleRouteMarkerClick = (l: Lugar) => {
    if (captureMode) return;
    setSelectedRoutePlaces((prev) => {
      if (prev.some((p) => p.id === l.id)) return prev.filter((p) => p.id !== l.id);
      const next = [...prev, l];
      if (next.length > 2) next.shift();
      return next;
    });
    itineraryItemRefs.current[l.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleExternalMarkerClick = (place: Lugar) => {
    setSelectedNewPlace((prev) => (prev?.id === place.id ? null : place));
  };

  const handleNewPlaceClick = (l: Lugar) => {
    if (captureMode) return;
    setSelectedNewPlace((prev) => (prev?.id === l.id ? null : l));
  };

  const isMarkerSelected = (l: Lugar) =>
    selectedRoutePlaces.some((p) => p.id === l.id) || selectedNewPlace?.id === l.id;

  /* ------------------------------------------------------------------ */
  /*  Flags de disponibilidad de botones                                 */
  /* ------------------------------------------------------------------ */
  const isRecalculateDisabled = !(selectedRoutePlaces.length === 1 && selectedNewPlace);
  const isDeleteDisabled = !(selectedRoutePlaces.length === 1 && !selectedNewPlace);

  const itinerarioSeleccionado =
    selectedDay !== null ? itinerarios.find((it) => it.dia === selectedDay) : null;

  /* ------------------------------------------------------------------ */
  /*  Acciones de botones                                                */
  /* ------------------------------------------------------------------ */
  const eliminarPuntoSeleccionado = async () => {
    if (!itinerarioSeleccionado || selectedRoutePlaces.length !== 1) return;
    try {
      const it = await eliminarLugarItinerario(
        itinerarioSeleccionado.dia,
        selectedRoutePlaces[0].id,
        itinerarioSeleccionado.lugares,
      );
      setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
      setSelectedRoutePlaces([]);
      setWarningMessage(null);
    } catch {
      setWarningMessage('Error al eliminar.');
    }
  };

  const intercambiarLugares = async () => {
    if (!itinerarioSeleccionado || selectedRoutePlaces.length !== 1 || !selectedNewPlace) {
      return setWarningMessage('Selecciona 1 dentro y 1 fuera.');
    }
    try {
      const [oldL] = selectedRoutePlaces;
      const nuevos = itinerarioSeleccionado.lugares.map((l) => (l.id === oldL.id ? selectedNewPlace : l));
      const it = await recalcularItinerario(
        itinerarioSeleccionado.dia,
        nuevos,
        oldL,
        selectedNewPlace,
      );
      setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
      setSelectedNewPlace(null);
      setSelectedRoutePlaces([]);
      setWarningMessage(null);
    } catch {
      setWarningMessage('Error al intercambiar.');
    }
  };

  const sustituirLugarSinRecalcular = async () => {
    if (!itinerarioSeleccionado || selectedRoutePlaces.length !== 2) {
      return setWarningMessage('Selecciona 2 dentro.');
    }
    const [a, b] = selectedRoutePlaces;
    const copia = [...itinerarioSeleccionado.lugares];
    const iA = copia.findIndex((x) => x.id === a.id);
    const iB = copia.findIndex((x) => x.id === b.id);
    if (iA < 0 || iB < 0) return setWarningMessage('No encontrados.');
    [copia[iA], copia[iB]] = [copia[iB], copia[iA]];
    const di = itinerarios.findIndex((it) => it.dia === itinerarioSeleccionado.dia);
    const newIts = [...itinerarios];
    newIts[di] = { ...newIts[di], lugares: copia };
    setItinerarios(newIts);
    await actualizarGeometryDia(di, copia);
    setSelectedRoutePlaces([]);
    setSelectedNewPlace(null);
    setWarningMessage(null);
  };

  /* ---------- estado de arrastre ---------- */
  const [draggedPlace, setDraggedPlace] = useState<{ place: Lugar; day: number } | null>(null);

  const onPlaceDragStart = (e: React.DragEvent, place: Lugar, day: number) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedPlace({ place, day });
  };

  const onPlaceDragOver = (e: React.DragEvent) => e.preventDefault();

  const onPlaceDrop = async (e: React.DragEvent, target: Lugar, targetDay: number) => {
    e.stopPropagation();
    if (!draggedPlace) return;
    const { place: src, day: srcDay } = draggedPlace;
    if (src.id === target.id) return;

    if (srcDay === targetDay) {
      reordenarDentroDelDia(srcDay, src.id, target.id);
    } else {
      await intercambiarEntreDias(srcDay, targetDay, src.id, target.id);
    }
    setDraggedPlace(null);
  };

  const handlePlaceDropToEmpty = async (destDay: number) => {
    if (!draggedPlace || draggedPlace.day === destDay) return;
    await intercambiarEntreDias(draggedPlace.day, destDay, draggedPlace.place.id, null);
    setDraggedPlace(null);
  };

  function reordenarDentroDelDia(day: number, idA: number, idB: number) {
    setItinerarios((prev) => {
      const copy = [...prev];
      const di = copy.findIndex((it) => it.dia === day);
      if (di === -1) return prev;

      const lugares = [...copy[di].lugares];
      const idxA = lugares.findIndex((l) => l.id === idA);
      const idxB = lugares.findIndex((l) => l.id === idB);
      if (idxA === -1 || idxB === -1) return prev;

      [lugares[idxA], lugares[idxB]] = [lugares[idxB], lugares[idxA]];
      copy[di] = { ...copy[di], lugares };
      return copy;
    });
  }

  async function intercambiarEntreDias(srcDay: number, dstDay: number, idSrc: number, idDst: number | null) {
    const its = [...itinerarios];

    const iSrc = its.findIndex((it) => it.dia === srcDay);
    const iDst = its.findIndex((it) => it.dia === dstDay);
    if (iSrc === -1 || iDst === -1) return;

    const srcPlaces = [...its[iSrc].lugares];
    const dstPlaces = [...its[iDst].lugares];

    const idxSrc = srcPlaces.findIndex((l) => l.id === idSrc);
    if (idxSrc === -1) return;
    const [placeSrc] = srcPlaces.splice(idxSrc, 1);

    if (idDst === null) {
      dstPlaces.push(placeSrc);
    } else {
      const idxDst = dstPlaces.findIndex((l) => l.id === idDst);
      if (idxDst === -1) return;
      const [placeDst] = dstPlaces.splice(idxDst, 1, placeSrc);
      srcPlaces.splice(idxSrc, 0, placeDst);
    }

    its[iSrc] = { ...its[iSrc], lugares: srcPlaces };
    its[iDst] = { ...its[iDst], lugares: dstPlaces };
    setItinerarios(its);

    await Promise.all([actualizarGeometryDia(iSrc, srcPlaces), actualizarGeometryDia(iDst, dstPlaces)]);
  }

  async function actualizarGeometryDia(idx: number, lugDia: Lugar[]) {
    let coords = lugDia.map((l) => [l.latitud, l.longitud] as [number, number]);
    if (startingPoint)
      coords = [
        [startingPoint.latitud, startingPoint.longitud],
        ...coords,
        [startingPoint.latitud, startingPoint.longitud],
      ];
    const geo = await obtenerRutaOSRM(coords);
    if (geo) {
      setRutasOSRM((rs) => {
        const c = [...rs];
        c[idx] = geo;
        return c;
      });
    }
  }

  const zoomToLocation = (lat: number, lon: number) => mapRef.current?.setView([lat, lon], 16);

  const handleSendEmail = async (toEmail: string) => {
    try {
      setSendingEmail(true);
      await shareItinerary({
        email: toEmail,
        itinerary: itinerarios,
        note: note.trim(),
      });
      alert('¡Correo enviado con éxito! Revisa tu bandeja de entrada.');
    } catch (err) {
      console.error('Error enviando correo:', err);
      alert('Hubo un problema al enviar el correo. Inténtalo más tarde.');
    } finally {
      setSendingEmail(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <div className={styles.container}>
      <div className={styles.languageSelector}></div>

      <div className={styles.sidebar}>
        <div className={styles.dayTabs}>
          {(itinerarios || []).map((it, idx) => {
            const color = colores[idx % colores.length];
            const desc = descripciones[it.dia];
            const loadingDesc = isLoadingDescriptions[it.dia];
            const activo = selectedDay === it.dia;

            return (
              <button
                key={it.dia}
                className={`${styles.dayTabButton} ${activo ? styles.activeDay : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedDay(it.dia)}
                draggable
                onDragStart={(e) => handleDragStart(e, it.dia)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, it.dia)}
              >
                DÍA&nbsp;{it.dia}
                <div className={styles.dayTabHover}>
                  {loadingDesc ? 'Cargando descripción…' : desc || 'No hay descripción.'}
                </div>
              </button>
            );
          })}
        </div>

        <div className={styles.scrollArea}>
          <div className={styles.itineraryGrid}>
            {itinerarios.map((it) => (
              <div
                key={it.dia}
                className={styles.dayColumn}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handlePlaceDropToEmpty(it.dia)}
              >
                {it.lugares.map((l) => (
                  <div
                    key={l.id}
                    ref={(el) => (itineraryItemRefs.current[l.id] = el)}
                    className={`${styles.placeCard} ${isMarkerSelected(l) ? styles.activeCard : ''}`}
                    draggable
                    onDragStart={(e) => onPlaceDragStart(e, l, it.dia)}
                    onDragOver={onPlaceDragOver}
                    onDrop={(e) => onPlaceDrop(e, l, it.dia)}
                    onDragEnd={() => setDraggedPlace(null)}
                  >
                    <img
                      src={getImageUrl(l.fotos)}
                      alt={l.nombre}
                      className={styles.placeThumbLarge}
                      onClick={() => {
                        setSelectedDay(it.dia);
                        setTimeout(() => {
                          mapRef.current?.setView([l.latitud, l.longitud], mapRef.current?.getMaxZoom());
                          handleRouteMarkerClick(l);
                        }, 300);
                      }}
                    />

                    <div className={styles.placeTitleAndTime}>
                      <div className={styles.placeTitle} title={l.nombre}>
                        {l.nombre}
                      </div>
                      <div className={styles.placeTimes}>
                        <span>Llegada: {formatearHora(l.horaLlegada)}</span>
                        <span>Salida:&nbsp;&nbsp;{formatearHora(l.horaSalida)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.main}>
        {warningMessage && (
          <div className={styles.warning}>
            <p>{warningMessage}</p>
            <button onClick={() => setWarningMessage(null)}>Cerrar</button>
          </div>
        )}

        <div className={styles.buttonContainer}>
          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (!(selectedRoutePlaces.length === 1 && selectedNewPlace)) setShowRecalculateTooltip(true);
            }}
            onMouseLeave={() => setShowRecalculateTooltip(false)}
          >
            <button
              onClick={intercambiarLugares}
              disabled={!(selectedRoutePlaces.length === 1 && selectedNewPlace)}
              className={
                !(selectedRoutePlaces.length === 1 && selectedNewPlace)
                  ? styles.disabledButton
                  : styles.enabledButton
              }
            >
              Cambiar Ruta
            </button>
            {!(selectedRoutePlaces.length === 1 && selectedNewPlace) && showRecalculateTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona 1 punto de la ruta y 1 fuera para intercambiarlos.</p>
              </div>
            )}
          </div>

          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (selectedRoutePlaces.length !== 2) setShowSustituirTooltip(true);
            }}
            onMouseLeave={() => setShowSustituirTooltip(false)}
          >
            <button
              onClick={sustituirLugarSinRecalcular}
              disabled={selectedRoutePlaces.length !== 2}
              className={selectedRoutePlaces.length !== 2 ? styles.disabledButton : styles.enabledButton}
            >
              Intercambiar puntos en la ruta
            </button>
            {selectedRoutePlaces.length !== 2 && showSustituirTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona 2 lugares de la ruta para intercambiarlos de orden.</p>
              </div>
            )}
          </div>

          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (!(selectedRoutePlaces.length === 1 && !selectedNewPlace)) setShowDeleteTooltip(true);
            }}
            onMouseLeave={() => setShowDeleteTooltip(false)}
          >
            <button
              onClick={eliminarPuntoSeleccionado}
              disabled={!(selectedRoutePlaces.length === 1 && !selectedNewPlace)}
              className={
                !(selectedRoutePlaces.length === 1 && !selectedNewPlace)
                  ? styles.disabledButton
                  : styles.enabledButton
              }
            >
              Eliminar Punto
            </button>
            {!(selectedRoutePlaces.length === 1 && !selectedNewPlace) && showDeleteTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona 1 punto de la ruta para eliminarlo.</p>
              </div>
            )}
          </div>

          <div className={styles.buttonWrapper}>
            <button
              onClick={() => setShowEmailModal(true)}
              disabled={sendingEmail}
              className={sendingEmail ? styles.disabledButton : styles.enabledButton}
            >
              {sendingEmail ? 'Enviando…' : 'Enviar ruta por correo'}
            </button>
          </div>
        </div>

        <MapContainer center={centroMapa} zoom={12} preferCanvas className={styles.map}>
          <SetMapRef mapRef={mapRef} />
          <MapClickHandler onClick={() => setActivePlace(null)} />

          <TileLayer
            attribution={CARTO_ATTRIBUTION}
            url={CARTO_BASEMAP_URL}
          />

          <ShapefileOverlay url="/data/Municipios.zip" fitOnLoad={false} />

          {startingPoint && (
            <Marker position={[startingPoint.latitud, startingPoint.longitud]} icon={houseIcon}>
              {!captureMode && (
                <Popup
                  autoClose={false}
                  closeOnClick={false}
                  closeButton={false}
                  offset={[0, -40]}
                  className={styles.fondo}
                >
                  <div className={styles.tooltipContent}>
                    <h3>Punto de Inicio</h3>
                    <p>{startingPoint.nombre}</p>
                  </div>
                </Popup>
              )}
            </Marker>
          )}

          {selectedDay !== null && (
            <>
              {dayStarts[selectedDay] && (
                <Marker
                  position={[dayStarts[selectedDay]!.latitud, dayStarts[selectedDay]!.longitud]}
                  icon={houseIcon}
                >
                  {!captureMode && (
                    <Popup
                      autoClose={false}
                      closeOnClick={false}
                      closeButton={false}
                      offset={[0, -40]}
                      className={styles.fondo}
                    >
                      <div className={styles.tooltipContent}>
                        <h3>Inicio día {selectedDay}</h3>
                      </div>
                    </Popup>
                  )}
                </Marker>
              )}

              {dayEnds[selectedDay] && (
                <Marker
                  position={[dayEnds[selectedDay]!.latitud, dayEnds[selectedDay]!.longitud]}
                  icon={houseIcon}
                >
                  {!captureMode && (
                    <Popup
                      autoClose={false}
                      closeOnClick={false}
                      closeButton={false}
                      offset={[0, -40]}
                      className={styles.fondo}
                    >
                      <div className={styles.tooltipContent}>
                        <h3>Fin día {selectedDay}</h3>
                      </div>
                    </Popup>
                  )}
                </Marker>
              )}
            </>
          )}

          {selectedDay !== null &&
            rutasOSRM.map((geo, di) => {
              if (itinerarios[di].dia !== selectedDay) return null;

              const color = colores[di % colores.length];
              const pos = geo.coordinates.map(([lon, lat]) => [lat, lon] as LatLngTuple);

              return (
                <React.Fragment key={di}>
                  <Polyline positions={pos} color={color} weight={4} opacity={1} />

                  {itinerarios[di].lugares.map((l, i) => {
                    let ico = getIconForLugarType(l.tipo);
                    if (isMarkerSelected(l)) ico = blueIcon;
                    const numbered = createNumberedIcon(i + 1, ico, color);

                    return (
                      <Marker
                        key={l.id}
                        position={[l.latitud, l.longitud]}
                        icon={numbered}
                        eventHandlers={{
                          click: () => handleRouteMarkerClick(l),
                          mouseover: () => {
                            if (captureMode) return;
                            cancelClosePopup();
                            setActivePlace(l);
                          },
                          mouseout: () => {
                            if (captureMode) return;
                            scheduleClosePopup();
                          },
                        }}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}

          <ClusterLayer
            puntos={puntosTuristicos.filter((pt) => !lugaresEnItinerarios.has(pt.id))}
            selectedRoutePlaces={selectedRoutePlaces}
            selectedNewPlace={selectedNewPlace}
            onMarkerClick={handleExternalMarkerClick}
            onMarkerMouseOver={(p) => {
              cancelClosePopup();
              setActivePlace(p);
            }}
            onMarkerMouseOut={() => {
              scheduleClosePopup();
            }}
          />

          {!captureMode && activePlace && (
            <Popup
              position={[activePlace.latitud, activePlace.longitud]}
              autoClose={false}
              closeOnClick={false}
              closeButton={false}
              offset={[0, -40]}
              className={styles.fondo}
            >
              <div
                className={styles.popupContent}
                onMouseEnter={() => cancelClosePopup()}
                onMouseLeave={() => scheduleClosePopup()}
              >
                <h3>{activePlace.nombre}</h3>
                <img src={getImageUrl(activePlace.fotos)} alt={activePlace.nombre} />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPlaceCardPlace(activePlace);
                  }}
                >
                  Ver detalles
                </button>
              </div>
            </Popup>
          )}
        </MapContainer>
      </div>

      {placeCardPlace && (
        <div className={styles.modalOverlay} onClick={() => setPlaceCardPlace(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <PlaceCard lugar={placeCardPlace} onClose={() => setPlaceCardPlace(null)} />
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className={styles.modalOverlay} onClick={() => setShowEmailModal(false)}>
          <div
            className={`${styles.modalContent} ${styles.emailModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>¿A qué correo quieres enviarlo?</h3>

            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="tu@email.com"
              className={styles.emailInput}
            />

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Añade un mensaje para el destinatario (opcional)"
              rows={3}
              className={styles.emailNote}
            />

            <div className={styles.modalActions}>
              <button onClick={() => setShowEmailModal(false)}>Cancelar</button>
              <button
                disabled={!/\S+@\S+\.\S+/.test(recipientEmail) || sendingEmail}
                onClick={() => {
                  setShowEmailModal(false);
                  handleSendEmail(recipientEmail);
                }}
              >
                {sendingEmail ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className={styles.footer}>
        <div className={styles.footerLogos}>
          <img src={logosImg} alt="NextGenerationEU" />
        </div>
      </footer>
    </div>
  );
};

export default MapView;
